import React, { useMemo } from "react";
import { longDateString } from "../../utils";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import "../../starek.css";

function OperationsCardView({
    items,
    selectedRows,
    onSelectedRowsChange,
    filterText,
    onAdd,
    onRowClicked,
    onToggleActive,
    togglingActiveId,
}) {
    const isOperationSelected = (operationId) => {
        return selectedRows.some((row) => row.id === operationId);
    };

    const handleCheckboxChange = (operation) => {
        let newSelectedRows;
        if (isOperationSelected(operation.id)) {
            newSelectedRows = selectedRows.filter(
                (row) => row.id !== operation.id,
            );
        } else {
            newSelectedRows = [...selectedRows, operation];
        }
        if (onSelectedRowsChange) {
            onSelectedRowsChange({ selectedRows: newSelectedRows });
        }
    };

    const filteredItems = useMemo(() => {
        const q = filterText?.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [items, filterText]);

    if (filteredItems.length === 0) {
        return (
            <EntityEmptyState
                icon="fa-tasks"
                entityName="Operation"
                messages={EMPTY_MESSAGES.operations}
                isFiltered={!!filterText?.trim()}
                onAdd={onAdd}
            />
        );
    }

    return (
        <div className="row g-3">
            {filteredItems.map((operation) => (
                <div key={operation.id} className="col-lg-3 col-md-4 col-sm-6">
                    <div
                        className={`card h-100 shadow-sm ${
                            isOperationSelected(operation.id)
                                ? "border-primary border-2"
                                : ""
                        }`}
                        style={{
                            cursor: "pointer",
                            opacity: operation.is_active === false ? 0.55 : 1,
                        }}
                        onClick={() => onRowClicked?.(operation)}
                    >
                        <div className="card-header bg-primary text-white">
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center flex-grow-1">
                                    <div
                                        className="form-check mb-0 me-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={isOperationSelected(
                                                operation.id,
                                            )}
                                            onChange={() =>
                                                handleCheckboxChange(operation)
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label={`Select ${operation.name}`}
                                        />
                                    </div>
                                    <h5
                                        className="card-title mb-0 text-truncate"
                                        title={operation.name}
                                    >
                                        {operation.name}
                                    </h5>
                                </div>
                                <div
                                    className="d-flex align-items-center gap-1"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {onToggleActive && (
                                        <button
                                            className={`btn btn-sm ${operation.is_active ? "btn-outline-light" : "btn-outline-warning"}`}
                                            style={{
                                                padding: "2px 6px",
                                                fontSize: "0.7rem",
                                            }}
                                            title={
                                                operation.is_active
                                                    ? "Deactivate"
                                                    : "Restore"
                                            }
                                            disabled={
                                                togglingActiveId ===
                                                operation.id
                                            }
                                            onClick={() =>
                                                onToggleActive(operation)
                                            }
                                        >
                                            {togglingActiveId ===
                                            operation.id ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                <i
                                                    className={`fa ${operation.is_active ? "fa-ban" : "fa-undo"}`}
                                                />
                                            )}
                                        </button>
                                    )}
                                    <i className="fa fa-tasks fa-lg"></i>
                                </div>
                            </div>
                        </div>
                        <div className="card-body d-flex flex-column">
                            <p
                                className="text-muted mb-3"
                                style={{ fontSize: "0.875rem" }}
                            >
                                {operation.description || "No description"}
                            </p>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                <span
                                    className={`badge bg-${operation.is_productive ? "success" : "danger"}`}
                                >
                                    {operation.is_productive
                                        ? "Productive"
                                        : "Non-productive"}
                                </span>
                            </div>
                            <div className="border-top pt-2 mt-auto">
                                <small className="text-muted d-block">
                                    <i className="fas fa-user me-1"></i>
                                    {operation.updated_by?.name || "Unknown"}
                                </small>
                                <small className="text-muted d-block mt-1">
                                    <i className="fas fa-clock me-1"></i>
                                    {longDateString(operation.updated_at)}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default OperationsCardView;
